import React, {useState, useEffect, useMemo} from 'react';
import {TrashIcon} from '@heroicons/react/24/solid';
import {Button, Modal, Input, Select} from "antd";
import axiosInstance from "../axios/axiosInstance";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import InputMask from "react-input-mask";
import {debounce} from "lodash";
import { useTranslation } from 'react-i18next';
import CalendarDashboard from "./calendar/CalendarDashboard";
import {
    buildCalendarPayload,
    buildDoctorColorMap,
    mapApiCalendarEvent,
    pickDoctorsFromUsers,
} from "./calendar/calendarUtils";

export default function Calendar() {
    const { t } = useTranslation();
    const readOnly = localStorage.getItem('userRole') === 'doctor';
    const [eventTitle, setEventTitle] = useState('');
    const [eventDescription, setEventDescription] = useState('');
    const [eventNumber, setEventNumber] = useState('998');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [patientOptions, setPatientOptions] = useState([]);
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);
    const [currentDate, setCurrentDate] = useState(() => dayjs());
    const [layoutView, setLayoutView] = useState('grid');
    const [boardViewMode, setBoardViewMode] = useState('day');

    const colorMap = useMemo(() => {
        if (doctors.length) return buildDoctorColorMap(doctors);
        const unique = [];
        const seen = new Set();
        calendarEvents.forEach((e) => {
            if (e.doctor_id == null || seen.has(e.doctor_id)) return;
            seen.add(e.doctor_id);
            unique.push({ id: e.doctor_id, name: e.doctor?.name });
        });
        return buildDoctorColorMap(unique);
    }, [doctors, calendarEvents]);

    useEffect(() => {
        fetchEvents();
        if (!readOnly) {
            fetchDoctors();
        }
    }, [readOnly]);

    const fetchDoctors = async () => {
        try {
            const response = await axiosInstance.get("/admin/users");
            setDoctors(pickDoctorsFromUsers(response.data.data));
        } catch (error) {
            console.error("Ошибка при получении списка врачей:", error);
            setDoctors([]);
        }
    };

    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get("/calendar");
            const fetchedEvents = response.data.data[0];
            if (Array.isArray(fetchedEvents)) {
                setCalendarEvents(fetchedEvents.map(mapApiCalendarEvent));
            } else {
                setCalendarEvents([]);
            }
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateModal = (date, doctorId = null) => {
        if (readOnly) return;
        resetEventData();
        const d = date ?? currentDate;
        setSelectedDate(d.format('YYYY-MM-DD'));
        setSelectedDoctorId(doctorId);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        resetEventData();
        setOpenDialog(false);
    };

    const resetEventData = () => {
        setEventTitle('');
        setEventNumber('998');
        setSelectedEvent(null);
        setEventDescription('');
        setSelectedPatientId(null);
        setPatientOptions([]);
        setSelectedDoctorId(null);
        setErrors({});
    };

    const searchPatients = debounce(async (text) => {
        if (!text || text.trim().length < 2) {
            setPatientOptions([]);
            return;
        }
        setPatientsLoading(true);
        try {
            const response = await axiosInstance.get('/patients', {
                params: {name: text.trim(), pageSize: 20},
            });
            const list = response.data.data ?? [];
            setPatientOptions(
                list.map((p) => ({
                    value: p.id,
                    label: `${p.name}${p.phone ? ` · ${p.phone}` : ''}`,
                    patient: p,
                }))
            );
        } catch (error) {
            console.error('Patient search failed:', error);
        } finally {
            setPatientsLoading(false);
        }
    }, 400);

    const handlePatientSelect = (patientId) => {
        setSelectedPatientId(patientId ?? null);
        const option = patientOptions.find((o) => o.value === patientId);
        if (option?.patient) {
            setEventTitle(option.patient.name ?? '');
            if (option.patient.phone) {
                setEventNumber(String(option.patient.phone).replace(/\D/g, ''));
            }
        }
    };

    const openEventModal = (event) => {
        setEventTitle(event.title);
        setEventNumber(event.phone ? String(event.phone).replace(/\D/g, '') : '998');
        setSelectedDate(event.date);
        setEventDescription(event.description || '');
        setSelectedPatientId(event.patient_id ?? null);
        if (event.patient_id) {
            setPatientOptions([{value: event.patient_id, label: event.title}]);
        }
        setSelectedDoctorId(event.doctor_id ?? null);
        setSelectedEvent(event);
        setOpenDialog(true);
    };

    const handleDeleteEvent = async () => {
        if (readOnly || !selectedEvent?.id) return;
        try {
            const response = await axiosInstance.delete(`/calendar/${selectedEvent.id}`);
            if (response.status === 200) {
                fetchEvents();
                handleDialogClose();
            }
        } catch (error) {
            console.error("Ошибка при удалении события:", error);
        }
    };

    const handleUpdateEvent = async () => {
        if (readOnly || validateFields()) return;
        const startAt = selectedDate.split('T')[0];
        const updatedEventData = buildCalendarPayload({
            eventTitle,
            eventNumber,
            formattedDate: startAt,
            eventDescription,
            selectedPatientId,
            selectedDoctorId,
        });
        try {
            const response = await axiosInstance.put(`/calendar/${selectedEvent.id}`, updatedEventData);
            if (response.status === 200) {
                fetchEvents();
                toast.success(t('calendar.updated'));
                handleDialogClose();
            }
        } catch (error) {
            console.error("Ошибка при обновлении события:", error);
            toast.error(t('calendar.updateError'));
        }
    };

    const handleConfirmEvent = async () => {
        if (readOnly || validateFields()) return;
        if (!selectedDate) {
            toast.error(t('calendar.pickDateError'));
            return;
        }
        const formattedDate = selectedDate.split('T')[0];
        const eventData = buildCalendarPayload({
            eventTitle,
            eventNumber,
            formattedDate,
            eventDescription,
            selectedPatientId,
            selectedDoctorId,
        });
        try {
            await axiosInstance.post("/calendar", eventData);
            fetchEvents();
            handleDialogClose();
            toast.success(t('calendar.created'));
        } catch (error) {
            console.error("Ошибка при создании события:", error);
            toast.error(t('calendar.createError'));
        }
    };

    const validateFields = () => {
        const newErrors = {};
        let hasError = false;
        if (!eventTitle.trim()) {
            newErrors.eventTitle = t('calendar.fioRequired');
            hasError = true;
        }
        if (!eventNumber.trim() || eventNumber.length < 10) {
            newErrors.eventNumber = t('calendar.phoneRequired');
            hasError = true;
        }
        if (!selectedDate) {
            newErrors.selectedDate = t('calendar.dateRequired');
            hasError = true;
        }
        setErrors(newErrors);
        return hasError;
    };

    return (
        <>
            <CalendarDashboard
                loading={isLoading}
                currentDate={currentDate}
                onDateChange={setCurrentDate}
                layoutView={readOnly ? 'grid' : layoutView}
                onLayoutViewChange={setLayoutView}
                boardViewMode={boardViewMode}
                onBoardViewModeChange={setBoardViewMode}
                events={calendarEvents}
                doctors={doctors}
                colorMap={colorMap}
                searchText={searchText}
                onSearchChange={setSearchText}
                onEventClick={openEventModal}
                onAddClick={readOnly ? undefined : (doctorId, date) => openCreateModal(date ?? currentDate, doctorId)}
                readOnly={readOnly}
            />

            <Modal
                title={selectedEvent ? (readOnly ? t('calendar.viewVisit') : t('calendar.editVisit')) : t('calendar.newVisit')}
                centered
                width="100%"
                style={{ maxWidth: 480 }}
                open={openDialog}
                onCancel={handleDialogClose}
                footer={
                    readOnly ? (
                        <Button block onClick={handleDialogClose}>
                            {t('common.close')}
                        </Button>
                    ) : [
                    <div key="footer" className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
                        <Button
                            block
                            type="primary"
                            className="flex w-full justify-center items-center font-medium"
                            onClick={selectedEvent ? handleUpdateEvent : handleConfirmEvent}
                        >
                            {selectedEvent ? t('calendar.update') : t('calendar.save')}
                        </Button>
                        {selectedEvent && (
                            <Button
                                block
                                className="flex w-full justify-center items-center font-medium bg-gray-100 text-gray-800"
                                onClick={handleDeleteEvent}
                            >
                                {t('calendar.deleteEvent')}
                                <TrashIcon className="h-4 w-4 ml-2"/>
                            </Button>
                        )}
                    </div>,
                ]
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">{t('calendar.date')}</label>
                        <Input
                            type="date"
                            size="large"
                            disabled={readOnly}
                            value={selectedDate?.split('T')[0] ?? ''}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                        {errors.selectedDate && <p className="text-red-500 text-xs mt-1">{errors.selectedDate}</p>}
                    </div>

                    <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">{t('calendar.fio')}</label>
                        <Input
                            value={eventTitle}
                            disabled={readOnly}
                            onChange={(e) => setEventTitle(e.target.value)}
                            placeholder={t('calendar.fio')}
                            size="large"
                        />
                        {errors.eventTitle && <p className="text-red-500 text-xs mt-1">{errors.eventTitle}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('calendar.phone')}</label>
                        <InputMask
                            mask="9\9\8 (99) 999-99-99"
                            maskChar="_"
                            value={eventNumber}
                            disabled={readOnly}
                            onChange={(e) => setEventNumber(e.target.value.replace(/[^\d]/g, ''))}
                        >
                            {(inputProps) => (
                                <Input
                                    {...inputProps}
                                    size="large"
                                    disabled={readOnly}
                                    prefix={<img src="/icons8-узбекистан-48.png" className="w-6 h-6" alt=""/>}
                                />
                            )}
                        </InputMask>
                        {errors.eventNumber && <p className="text-red-500 text-xs mt-1">{errors.eventNumber}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('calendar.patientOptional')}</label>
                        <Select
                            showSearch
                            allowClear
                            filterOption={false}
                            className="w-full"
                            size="large"
                            disabled={readOnly}
                            placeholder={t('calendar.searchPatient')}
                            value={selectedPatientId}
                            options={patientOptions}
                            loading={patientsLoading}
                            onSearch={searchPatients}
                            onChange={handlePatientSelect}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('calendar.doctorOptional')}</label>
                        <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            className="w-full"
                            size="large"
                            disabled={readOnly}
                            placeholder={t('calendar.pickDoctor')}
                            value={selectedDoctorId}
                            options={[
                                ...doctors.map((d) => ({value: d.id, label: d.name})),
                                ...(selectedEvent?.doctor && !doctors.some((d) => d.id === selectedEvent.doctor.id)
                                    ? [{value: selectedEvent.doctor.id, label: selectedEvent.doctor.name}]
                                    : []),
                            ]}
                            onChange={(value) => setSelectedDoctorId(value ?? null)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('calendar.description')}</label>
                        <Input.TextArea
                            value={eventDescription}
                            disabled={readOnly}
                            onChange={(e) => setEventDescription(e.target.value)}
                            placeholder={t('calendar.descriptionPh')}
                            rows={4}
                            size="large"
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
}
